import { CircularProgress } from '@material-ui/core';
import Compressor from 'compressorjs';
import React, { useMemo, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import cameraIcon from 'images/cameraIcon.svg';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Meta } from 'types/meta';
import { Review } from 'types/review';
import { TermsOfUse } from 'common/components/TermsOfUse';
import { animation, animationFunction } from 'styles/Form/animation';
import { ButtonBody } from 'styles/Form/ButtonBody';
import { ButtonText } from 'styles/Form/ButtonText';
import { Divider } from 'styles/Form/Divider';
import { Location, NetworkReference } from 'types/location';
import { FormButton } from 'styles/Form/FormButton';
import { Rating } from 'styles/Form/Rating';
import { RatingText } from 'styles/Form/RatingText';
import { ReviewButtonIcon } from 'styles/Form/ReviewButtonIcon';
import { makeStyles } from '@material-ui/core';
import {
  ReviewImage,
  ReviewImageContainer,
  ReviewImageOverlayIcon
} from 'styles/Form/ReviewImage';
import { TwoButtonContainer } from 'styles/Form/TwoButtonContainer';
import { TextInputContainer } from 'styles/Form/TextInputContainer';
import { Title } from 'styles/Title';
import { FileInput } from 'common/components/FileInput';
import { RatingStar } from 'styles/Form/RatingStar';
import { css } from 'styled-components';
import { ShareBlock } from 'client/components/FormComponents/ShareBlock';
import { LocationSelectionBlock } from 'client/components/FormComponents/LocationSelectionBlock';

const MAX_SIZE = 999000;

interface FirstStageProps {
  meta: Meta;
  locations: Location[]; // present only for a company
  ratingLinks: NetworkReference[]; // present only for a location
  review: Review;
  setReview: React.Dispatch<React.SetStateAction<Review>>;
  assessment_question: string | null;
  nextStage: () => void;
}

export const FirstStage: FC<FirstStageProps> = ({
  meta,
  locations,
  ratingLinks,
  review,
  setReview,
  assessment_question,
  nextStage
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [starHovered, setStarHovered] = useState(-1);
  const [imageLoading, setImageLoading] = useState(false);
  const [reviewSendingError, setReviewSendingError] = useState(false);
  const [reviewSendingErrorText, setReviewSendingErrorText] = useState('');
  const isRatingValid = review.rating >= 0;
  const showSharing = review.ratingIncreaseStrategy && review.rating === 4;

  const ratings = [
    t('ratingVeryBad'),
    t('ratingBad'),
    t('ratingMiddle'),
    t('ratingGood'),
    t('ratingVeryGood')
  ];

  const ratingText = review.ratingError
    ? t('markIsRequired')
    : review.rating == -1
    ? t('chooseMark')
    : ratings[review.rating];

  const reviewPhotoUrl = useMemo(
    () => (review.photo ? URL.createObjectURL(review.photo) : null),
    [review.photo]
  );

  const changeLocation = (location: Location) => {
    setReview({
      ...review,
      contacts_required: location.contacts_required,
      hash: location.hash
    });
  };

  return (
    <>
      {assessment_question && <Title>{assessment_question}</Title>}
      <RatingText color={meta.color}>{ratingText}</RatingText>
      <Rating>
        {ratings.map((_, rating) => (
          <RatingStar
            css={css`
              animation: ${animation} 0.5s ${0.5 + rating / 20}s backwards;
              ${animationFunction}
            `}
            icon={
              rating <= review.rating || rating <= starHovered
                ? 'star'
                : 'star-empty'
            }
            color={
              review.ratingError && starHovered === -1 ? '#d90429' : meta.color
            }
            iconSize={40}
            key={rating}
            onClick={() =>
              setReview({
                ...review,
                rating,
                ratingError: false
              })
            }
            onMouseOver={() => setStarHovered(rating)}
            onMouseLeave={() => setStarHovered(-1)}
          />
        ))}
      </Rating>
      {locations.length > 0 && (
        <LocationSelectionBlock
          meta={meta}
          locations={locations}
          changeLocation={changeLocation}
        />
      )}
      {!showSharing && (
        <>
          <TextInputContainer>
            <TextareaAutosize
              placeholder={t('inputFeedback')}
              style={{ border: '1px solid #ececec' }}
              className={classes.feedbackInput}
              minRows={2}
              maxRows={40}
              onChange={({ target: { value: body } }) =>
                setReview({ ...review, body })
              }
            />
          </TextInputContainer>
          <TwoButtonContainer>
            <FormButton
              invert
              color={meta.color}
              style={{ width: '50px', height: '50px' }}
            >
              {reviewPhotoUrl ? (
                <ReviewImageContainer
                  onClick={() => {
                    URL.revokeObjectURL(reviewPhotoUrl);
                    setReview({ ...review, photo: null });
                  }}
                >
                  <ReviewImage src={reviewPhotoUrl} />
                  <ReviewImageOverlayIcon
                    icon="small-cross"
                    iconSize={50}
                    color="#ffffff"
                  />
                </ReviewImageContainer>
              ) : (
                <FileInput
                  disabled={imageLoading}
                  accept="image/*"
                  onChange={photo => {
                    setImageLoading(true);
                    if (photo.size > MAX_SIZE)
                      new Compressor(photo, {
                        quality: 0.8,
                        success(result) {
                          if (result instanceof Blob)
                            result = new File([result], photo.name);
                          setReview({ ...review, photo: result });
                          setImageLoading(false);
                        },
                        error(err) {
                          // eslint-disable-next-line no-console
                          console.log(err.message);
                          setReviewSendingErrorText(t('fileTooLarge'));
                          setReviewSendingError(true);
                          setImageLoading(false);
                        }
                      });
                    else setReview({ ...review, photo });
                  }}
                >
                  <ButtonBody>
                    {imageLoading ? (
                      <CircularProgress />
                    ) : (
                      <ReviewButtonIcon
                        source={cameraIcon}
                        color={meta.color}
                      />
                    )}
                  </ButtonBody>
                </FileInput>
              )}
            </FormButton>
            <Divider />
            <FormButton
              color={meta.color}
              style={{ flexGrow: 1 }}
              onClick={() => {
                if (review.sending) return;
                if (!isRatingValid)
                  return setReview({
                    ...review,
                    ratingError: true
                  });
                nextStage();
              }}
            >
              <ButtonBody>
                {review.sending ? (
                  <CircularProgress />
                ) : (
                  <ButtonText>{t('send')}</ButtonText>
                )}
              </ButtonBody>
            </FormButton>
          </TwoButtonContainer>
          <TermsOfUse />
        </>
      )}
      {showSharing && <ShareBlock networks={ratingLinks} />}
    </>
  );
};

export const useStyles = makeStyles({
  feedbackInput: {
    width: '100%',
    display: 'block',
    backgroundColor: '#f7f9fb',
    borderRadius: '10px',
    fontSize: '20px',
    padding: '15px',
    margin: '15px 0 0 0',
    '&:focus': {
      outline: 'none !important'
    },
    resize: 'none'
  }
});
